import { useSearchParams } from 'react-router-dom';
import {type CSSProperties, useState, useEffect } from 'react';

const SignInConfirmation: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [isRegistering, setIsRegistering] = useState(false);
    const [isEnrollingPasskey, setIsEnrollingPasskey] = useState(false);
    const [state, setState] = useState<string | null>(null);
    const [suggestPasskey, setSuggestPasskey] = useState(false);

    useEffect(() => {
        const stateParam = searchParams.get('state');
        const suggestPasskeyParam = searchParams.get('suggest_passkey');

        setState(stateParam);
        setSuggestPasskey(suggestPasskeyParam === 'true');

        console.log('SignIn Confirmation loaded:', {
            state: !!stateParam,
            suggestPasskey: suggestPasskeyParam === 'true'
        });
    }, [searchParams]);

    const handleRegister = (): void => {
        setIsRegistering(true);

        const continueUrl = new URL('https://login.themousy.org/continue');
        if (state) continueUrl.searchParams.set('state', state);
        continueUrl.searchParams.set('action', 'register');

        window.location.href = continueUrl.toString();
    };

    const handleEnrollPasskey = (): void => {
        setIsEnrollingPasskey(true);

        const continueUrl = new URL('https://login.themousy.org/continue');
        if (state) continueUrl.searchParams.set('state', state);
        continueUrl.searchParams.set('action', 'enroll_passkey');

        window.location.href = continueUrl.toString();
    };

    const handleSkipPasskey = (): void => {
        const continueUrl = new URL('https://login.themousy.org/continue');
        if (state) continueUrl.searchParams.set('state', state);
        continueUrl.searchParams.set('action', 'skip_passkey');

        window.location.href = continueUrl.toString();
    };

    const handleBackToHome = (): void => {
        const continueUrl = new URL('https://login.themousy.org/continue');
        if (state) continueUrl.searchParams.set('state', state);
        continueUrl.searchParams.set('action', 'back');

        window.location.href = continueUrl.toString();
    };

    const containerStyle: CSSProperties = {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        fontFamily: 'Arial, sans-serif'
    };

    const cardStyle: CSSProperties = {
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '90%'
    };

    const buttonStyle: CSSProperties = {
        padding: '15px 30px',
        fontSize: '18px',
        fontWeight: 'bold',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        margin: '15px',
        transition: 'all 0.2s',
        minWidth: '250px',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto'
    };

    const passkeyButtonStyle: CSSProperties = {
        ...buttonStyle,
        backgroundColor: '#6f42c1',
        color: 'white',
        fontSize: '20px',
        padding: '18px 35px'
    };

    const registerButtonStyle: CSSProperties = {
        ...buttonStyle,
        backgroundColor: '#28a745',
        color: 'white'
    };

    const secondaryButtonStyle: CSSProperties = {
        ...buttonStyle,
        backgroundColor: '#6c757d',
        color: 'white'
    };

    // Show passkey suggestion UI
    if (suggestPasskey) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>
                    <h1 style={{ color: '#333', marginBottom: '20px', fontSize: '2.5rem' }}>
                        Secure Your Account! 🔐
                    </h1>

                    <div style={{
                        padding: '20px',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '8px',
                        margin: '20px 0',
                        border: '1px solid #b3d9ff'
                    }}>
                        <h2 style={{ color: '#0c5460', margin: '0 0 15px 0', fontSize: '1.5rem' }}>
                            ✅ Sign-in Successful!
                        </h2>
                        <p style={{ color: '#0c5460', margin: 0, fontSize: '16px' }}>
                            Complete your account setup with a secure passkey.
                        </p>
                    </div>

                    <div style={{ margin: '40px 0' }}>
                        <div style={{
                            backgroundColor: '#fff3cd',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '30px',
                            border: '1px solid #ffeaa7'
                        }}>
                            <h3 style={{ color: '#856404', marginBottom: '15px' }}>
                                🚀 Set Up Passkey Authentication
                            </h3>
                            <p style={{ color: '#856404', margin: 0, fontSize: '14px' }}>
                                Use your fingerprint, face, or device PIN for secure, instant sign-in.
                                No more passwords or email codes needed!
                            </p>
                        </div>

                        <button
                            onClick={handleEnrollPasskey}
                            style={passkeyButtonStyle}
                            disabled={isEnrollingPasskey}
                        >
                            {isEnrollingPasskey ? '⏳ Setting up...' : '🔐 Set Up Passkey Now'}
                        </button>

                        <button
                            onClick={handleSkipPasskey}
                            style={secondaryButtonStyle}
                        >
                            ⏭️ Skip for Now
                        </button>
                    </div>

                    <div style={{ marginTop: '30px', fontSize: '13px', color: '#666' }}>
                        You can set up a passkey later in your account settings.
                    </div>
                </div>
            </div>
        );
    }

    // Regular confirmation UI
    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                <h1 style={{ color: '#333', marginBottom: '20px', fontSize: '2.5rem' }}>
                    Welcome to TheMousy! 🐭
                </h1>

                <div style={{
                    padding: '20px',
                    backgroundColor: '#d4edda',
                    borderRadius: '8px',
                    margin: '20px 0',
                    border: '1px solid #c3e6cb'
                }}>
                    <h2 style={{ color: '#155724', margin: '0 0 15px 0', fontSize: '1.5rem' }}>
                        ✅ Sign-in Successful!
                    </h2>
                    <p style={{ color: '#155724', margin: 0, fontSize: '16px' }}>
                        You have successfully signed in to TheMousy.
                    </p>
                </div>

                <div style={{ margin: '40px 0' }}>
                    <h3 style={{ marginBottom: '30px', color: '#333', fontSize: '1.3rem' }}>
                        Complete your setup
                    </h3>

                    <button
                        onClick={handleRegister}
                        style={registerButtonStyle}
                        disabled={isRegistering}
                    >
                        {isRegistering ? '⏳ Processing...' : '🎯 Complete Registration'}
                    </button>

                    <button
                        onClick={handleBackToHome}
                        style={secondaryButtonStyle}
                    >
                        🏠 Back to App
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignInConfirmation;